import React, { useEffect, useRef, useState } from "react";

const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

function useInViewOnce(margin = "-60px") {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            io.disconnect();
          }
        });
      },
      { rootMargin: `0px 0px ${margin} 0px`, threshold: 0 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [margin]);

  return [ref, inView];
}

const revealStyle = (inView, delay, y) => ({
  opacity: inView ? 1 : 0,
  transform: inView ? "none" : `translateY(${y}px)`,
  transition: `opacity 0.9s ${EASE} ${delay}s, transform 0.9s ${EASE} ${delay}s`,
  willChange: inView ? "auto" : "opacity, transform",
});

export const Reveal = ({ children, delay = 0, y = 32, className = "" }) => {
  const [ref, inView] = useInViewOnce("-80px");
  return (
    <div ref={ref} className={className} style={revealStyle(inView, delay, y)}>
      {children}
    </div>
  );
};

export const Stagger = ({ children, className = "", step = 0.12 }) => (
  <div className={className}>
    {React.Children.map(children, (child, i) =>
      React.isValidElement(child) ? React.cloneElement(child, { staggerDelay: i * step }) : child
    )}
  </div>
);

export const StaggerItem = ({ children, className = "", staggerDelay = 0 }) => {
  const [ref, inView] = useInViewOnce("-40px");
  return (
    <div ref={ref} className={className} style={revealStyle(inView, staggerDelay, 36)}>
      {children}
    </div>
  );
};
